package prescription.components

import antd.card.card
import antd.grid.col
import antd.grid.row
import antd.layout.space
import antd.message.message
import antd.radio.radio
import antd.radio.radioGroup
import antd.typography.paragraph
import antd.typography.title
import com.jingfangpai.common.orm.mapping.Prescription
import kotlinext.js.js
import react.*
import react.dom.div
import prescription.containers.PrescriptionListPageContext
import react.router.dom.useHistory
import urlBase
import external.pinyin.Pinyin
import kotlinx.browser.document
import kotlinx.html.id
import kotlinx.html.js.onClickFunction
import kotlinx.html.onClick
import react.dom.attrs
import react.dom.jsStyle

external interface PrescriptionListProps : RProps {

}

val PrescriptionList = functionalComponent<PrescriptionListProps> { props ->

    val ctx = useContext(PrescriptionListPageContext)
    val history = useHistory()

    val (prescriptionList, setPrescriptionList) = useState(listOf<Prescription>())
    val (sortType, setSortType) = useState("category") // "category" or "pinyin"

    fun getPrescriptionList() {
        ctx.getPrescriptionList({ resp ->
            if (resp.ok()) {
                setPrescriptionList(resp.data ?: listOf())
            } else {
                console.error("Failed to fetch prescription list:", resp)
                message.error("获取方剂列表失败:${resp.message ?: "未知错误"}")
            }
        }, { error ->
            console.error("Exception fetching prescription list:", error)
            console.error("Error stack:", error.stackTraceToString())
            val errorMsg = error.message ?: error.toString() ?: "未知错误"
            message.error("获取方剂列表失败:$errorMsg")
        })
    }

    useEffectOnce {
        getPrescriptionList()
    }

    // Group and sort prescriptions based on sortType
    val groupedPrescriptions = when (sortType) {
        "pinyin" -> {
            val grouped = prescriptionList.groupBy { prescription: Prescription ->
                val name = prescription.name ?: ""
                if (name.isNotEmpty()) {
                    try {
                        val spell = Pinyin.getSpell(name, null, null)
                        console.log(spell)
                        if (spell.startsWith("[")) {
                            val spells = spell.replace("[", "").replace("]", "").split(",")
                            spells[0].substring(0, 1).uppercase() // 默认第1个
                        } else {
                            spell.substring(0, 1).uppercase()
                        }
                    } catch (e: Exception) {
                        console.error("Pinyin conversion failed:", e)
                        name.substring(0, 1).uppercase()
                    }
                } else "#"
            }
            grouped.toList().sortedBy { it.first }.toMap().mapValues { it.value.sortedBy { med -> med.name ?: "" } }
        }
        "category" -> {
            val grouped = prescriptionList.groupBy { prescription: Prescription ->
                prescription.category?.getLiteral() ?: "其他"
            }
            grouped.toList().sortedBy { it.first }.toMap().mapValues { it.value.sortedBy { med -> med.name ?: "" } }
        }
        else -> mapOf("所有" to prescriptionList.sortedBy { it.name ?: "" })
    }

    div("PrescriptionList") {
        card {
            attrs {
                title = "方剂"
            }
            div {
                attrs.jsStyle = js {
                    position = "sticky"
                    top = "0"
                    backgroundColor = "white"
                    zIndex = 1000
                    paddingBottom = "10px"
                }
                radioGroup {
                    attrs {
                        value = sortType
                        onChange = {
                            setSortType(it.target.value as String)
                        }
                    }
                    radio {
                        attrs {
                            value = "category"
                        }
                        +"按分类排序"
                    }
                    radio {
                        attrs {
                            value = "pinyin"
                        }
                        +"按拼音排序"
                    }
                }
                // Index bar
                div {
                    attrs.jsStyle = js {
                        display = "flex"
                        flexWrap = "wrap"
                        gap = "5px"
                        marginTop = "10px"
                    }
                    groupedPrescriptions.keys.sorted().forEach { key ->
                        div {
                            this.attrs {
                                jsStyle = js {
                                    cursor = "pointer"
                                    padding = "5px 10px"
                                    backgroundColor = "#f0f0f0"
                                    borderRadius = "4px"
                                    fontSize = "14px"
                                }
                                onClickFunction = {
                                    val element = document.getElementById("group-$key")
                                    element?.scrollIntoView(js {
                                        behavior = "smooth"
                                        block = "start"
                                    })
                                }
                            }
                            +key
                        }
                    }
                }
            }
            space {
                attrs {
                    size = "middle"
                    direction = "vertical"
                }
            }
            if (prescriptionList.isEmpty()) {
                div {
                    +"暂无数据"
                }
            } else {
                groupedPrescriptions.forEach { (groupKey, prescriptions) ->
                    div {
                        attrs {
                            id = "group-$groupKey"
                            jsStyle = js {
                                marginBottom = "20px"
                            }
                        }
                        title {
                            attrs {
                                level = 4
                            }
                            +groupKey
                        }
                        row {
                            prescriptions.forEach { prescription ->
                                col {
                                    attrs {
                                        span = 6
                                    }
                                    card {
                                        attrs{
                                            hoverable = true
                                            onClick = {
                                                if (!prescription.id.isNullOrBlank()) {
                                                    history.push("$urlBase/learning/prescription/detail/${prescription.id}")
                                                }
                                            }
                                        }
                                        div {
                                            // 方剂名称
                                            title {
                                                attrs {
                                                    level = 4
                                                }
                                                +(prescription.name ?: "")
                                            }
                                            // 分类
                                            if (prescription.category != null) {
                                                div {
                                                    +"分类：${prescription.category?.getLiteral()}"
                                                }
                                            }
                                            // 出处
                                            if (!prescription.source.isNullOrBlank()) {
                                                div {
                                                    +"出处：${prescription.source}"
                                                }
                                            }
                                            // 功效
                                            if (!prescription.actions.isNullOrBlank()) {
                                                div {
                                                    +"功效："
                                                    paragraph {
                                                        attrs {
                                                            ellipsis = true
                                                        }
                                                        +(prescription.actions ?: "")
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
